<script lang="ts" setup>
defineOptions({ name: 'CareersPage' })

import { Icon } from '@/components/Icon'
import { jobs, jobBenefits } from '../data/portalData'
import { useRouter } from 'vue-router'
import { getPortalJobs } from '@/api/portal'

const router = useRouter()
const displayJobs = ref(jobs)

onMounted(async () => {
  try {
    const data = await getPortalJobs()
    if (data.list?.length) {
      displayJobs.value = data.list.map((job) => ({
        id: String(job.id),
        title: job.title,
        department: job.department || '招聘职位',
        deptColor: '#0066FF',
        location: job.workAddress || '面议',
        experience: job.workYears || '不限',
        education: job.education || '不限',
        salary: job.salary || '面议',
        desc: job.seoDescription || '',
        requirements: []
      }))
    }
  } catch {
    // CMS 未配置或暂不可用时保留当前门户内容。
  }
})

function goToDetail(jobId: string) {
  router.push(`/portal/careers/${jobId}`)
}
</script>

<template>
  <div class="careers-page">
    <!-- Hero Banner -->
    <div class="hero-banner">
      <div class="hero-overlay" />
      <div class="hero-content">
        <h1 class="hero-title">加入我们</h1>
        <p class="hero-subtitle">与优秀的人一起，做有价值的事</p>
      </div>
    </div>

    <div class="content-wrap">
      <!-- Benefits -->
      <div class="benefits-grid">
        <div v-for="benefit in jobBenefits" :key="benefit.text" class="benefit-item">
          <div class="benefit-icon" :style="{ background: benefit.bgColor }">
            <Icon :icon="benefit.icon" :size="24" :style="{ color: benefit.color }" />
          </div>
          <p class="benefit-text">{{ benefit.text }}</p>
        </div>
      </div>

      <!-- Job Listings -->
      <h2 class="jobs-title">热招职位</h2>
      <div class="jobs-grid">
        <div
          v-for="job in displayJobs"
          :key="job.id"
          class="job-card card-hover"
          @click="goToDetail(job.id)"
        >
          <div class="job-info">
            <div class="job-title-row">
              <h4 class="job-title">{{ job.title }}</h4>
              <span
                class="job-dept-tag"
                :style="{ background: job.deptColor + '1A', color: job.deptColor }"
              >
                {{ job.department }}
              </span>
            </div>
            <p class="job-meta">{{ job.location }} · {{ job.experience }} · {{ job.education }}</p>
          </div>
          <p class="job-salary">{{ job.salary }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.hero-banner {
  height: 280px;
  background: url('https://picsum.photos/seed/team/1280/280') center / cover;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 27, 94, 0.7);
}

.hero-content {
  position: relative;
  z-index: 2;
  text-align: center;
  color: #fff;
}

.hero-title {
  font-size: 36px;
  font-weight: 800;
  margin-bottom: 8px;
}

.hero-subtitle {
  font-size: 16px;
  opacity: 0.8;
}

.content-wrap {
  padding: 64px 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;
  margin-bottom: 48px;
}

.benefit-item {
  text-align: center;
}

.benefit-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 8px;
}

.benefit-text {
  font-size: 13px;
  font-weight: 500;
}

.jobs-title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 20px;
}

.jobs-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.job-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
}

.job-info {
  flex: 1;
}

.job-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.job-title {
  font-size: 15px;
  font-weight: 600;
}

.job-dept-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.job-meta {
  font-size: 13px;
  color: #6b7280;
}

.job-salary {
  font-size: 16px;
  font-weight: 700;
  color: #0066ff;
}

.card-hover {
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  }
}

@media (max-width: 1024px) {
  .benefits-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .jobs-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 28px;
  }

  .benefits-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  .job-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style>
